import { useCallback } from 'react';
import { FormRenderProps } from 'react-final-form';
import { Box, Stack } from '@mui/material';

import { useUserSelector } from '@occc-app/App/AppContext';
import { ProductPurchaseConfigure } from '@occc-app/routes/Offer/components/ProductPurchaseConfigure';
import { SpendBonusConfiguration } from '@occc-app/routes/Offer/components/SpendBonusConfiguration';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import OfferTypeSelectField from '@occc-app/routes/CampaignComponents/components/OfferTypeSelect';
import { offerTypes, offerTypeToAwardTypeMap } from '@occc-app/routes/Offer/constants';
import { Offer_Engagement_Type, OfferFilter, User_Role } from '@occc/graphql';
import { useConfig } from '@occc/config';
import { EarningMechanismAwardSelector } from '@occc-app/offerHelpers/components/earning-mechanism-award-selector';
import { AwardType, Flow_Types } from '@occc-app/offerHelpers/types';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';
import { VoucherExpirationDaysRedemption } from '@occc-app/routes/CampaignComponents/VoucherExpirationDaysRedemption';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { ScanDataConfiguration } from '@occc-app/routes/CampaignComponents/components/ScanDataConfiguration/ScanDataConfiguration';
import { EitherActivationOrAwardCount } from '@occc-app/routes/Promotion/components/PromotionConfigureForm/EitherActivationOrAwardCountProps';
import { StayVisibleWhileInProgress } from '@occc-app/routes/CampaignComponents/components/StayVisibleWhileInProgress';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { requiresActivationApplicableOfferTypes } from '@occc-app/routes/Offer/components/OfferConfigureForm/requiresActivationApplicableOfferTypes';
import {
  AwardRestrictions,
  AwardRestrictionsAwardTypeWatcher,
  EmailRequiredConstraint,
  FulfillmentChannelConstraint,
  FulfillmentWindow,
  MinimumPurchaseConstraints,
  PaymentMethodConstraint,
  RequiresActivationConstraintWithLimits,
} from '@occc/feat-constraints';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { EarningMechanismAwardPointsPer } from '@occc-app/offerHelpers/components/earning-mechanism-award-points-per';
import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { OfferCampaignField } from '@occc-app/routes/Offer/components/OfferCampaignField/offer-campaign-field.component';
import { OfferFulfillmentPriorityField } from '@occc-app/offerHelpers/components/OfferFulfillmentPriorityField';

import { OfferTypeWatcher } from './OfferTypeWatcher';
import {
  getDefaultTransactionFrequencyPeriod,
  getIsTransactionFrequencyLimitSupported,
  getIsTransactionLimitSupported,
} from './utils/offer.utils';

import { minimumPurchaseApplicableOfferTypes } from '../../constants';
import { OfferConfigureFormValues, OfferType } from '../../types';
import { BaseEarnConfiguration } from '../BaseEarnConfiguration';
import { BaseEligibleEarnConfiguration } from '../BaseEligibleEarnConfiguration';
import { configureFormCustomDataSubtitle } from '../../../../offerHelpers/copies';

type ConfigureFormContentProps = {
  onCancelClick: (hasUnsavedChanges?: boolean) => void;
  dirty: boolean;
  isFormEditable: boolean;
  currentFlowType: Flow_Types;
  submitting: boolean;
  onNextStepButtonClick?: () => void;
  form: FormRenderProps<OfferConfigureFormValues>['form'];
  values: OfferConfigureFormValues;
  makeOffersFilter: (search: string) => OfferFilter;
  handleSubmit: FormRenderProps<OfferConfigureFormValues>['handleSubmit'];
};

export const ConfigureFormContent = ({
  onCancelClick,
  dirty,
  isFormEditable,
  currentFlowType,
  submitting,
  onNextStepButtonClick,
  form,
  values,
  makeOffersFilter,
  handleSubmit,
}: ConfigureFormContentProps) => {
  const user = useUserSelector();
  const canEditCampaign = isFormEditable && user?.userRole === User_Role.Admin;
  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;

  const handleCancelClick = useCallback(() => {
    onCancelClick(dirty);
  }, [dirty, onCancelClick]);

  const formFooterActionButtons = isFormEditable ? (
    <CampaignComponentFooterActions
      currentFlowType={currentFlowType}
      onCancel={handleCancelClick}
      isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
    />
  ) : (
    <CampaignComponentReadOnlyFooterActions
      onCancel={handleCancelClick}
      onNextStepClick={onNextStepButtonClick}
    />
  );

  const onOfferTypeChange = useCallback(
    (value: OfferType | null) => {
      const awardType = value ? offerTypeToAwardTypeMap[value] : AwardType.AwardPoints;

      form.change('awardType', awardType);
    },
    [form],
  );

  const renderAwardSection = () => {
    const isAwardPointsPer =
      values.offerType === OfferType.BaseEarn || values.offerType === OfferType.BaseEligibleEarn;

    if (isAwardPointsPer) {
      return <EarningMechanismAwardPointsPer />;
    }

    if (values.offerType === OfferType.ProductPurchase) {
      return (
        <EarningMechanismAwardSelector
          buttonTypes={[AwardType.AwardExternal, AwardType.AwardPoints, AwardType.AwardVouchers]}
          makeOffersFilter={makeOffersFilter}
          values={{
            awardType: values.awardType,
          }}
        />
      );
    }

    if (values.offerType === OfferType.ExternalFulfillment) {
      return (
        <EarningMechanismAwardSelector
          buttonTypes={[AwardType.AwardNullable, AwardType.AwardPoints, AwardType.AwardVouchers]}
          makeOffersFilter={makeOffersFilter}
          values={{
            awardType: values.awardType,
          }}
        />
      );
    }

    return (
      <EarningMechanismAwardSelector
        buttonTypes={[AwardType.AwardPoints, AwardType.AwardVouchers]}
        makeOffersFilter={makeOffersFilter}
        values={{
          awardType: values.awardType,
        }}
      />
    );
  };

  const isVoucherRedemptionShown =
    (values.offerType === OfferType.ProductPurchase || values.offerType === OfferType.SpendBonus) &&
    values.awardType === AwardType.AwardVouchers;
  const isVoucherRedemptionApplicable =
    values.awardVouchers?.voucheredOffer?.engagementType === Offer_Engagement_Type.Vouchered;
  const isVoucherRedemptionEnabled = values.awardVouchers?.isEnabled ?? false;

  const shouldRenderRequiresActivationConstraint =
    !!values.offerType &&
    requiresActivationApplicableOfferTypes.includes(values.offerType) &&
    values.activation;
  const shouldRenderMinimumPurchaseConstraint =
    !!values.offerType && minimumPurchaseApplicableOfferTypes.includes(values.offerType);

  const isAwardRestrictionsShown =
    values.offerType !== OfferType.ExternalFulfillment &&
    values.awardType === AwardType.AwardVouchers;

  return (
    <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
      <OfferEarningMechanismConfigurationSection>
        <OfferTypeSelectField
          items={offerTypes}
          name='offerType'
          onChange={onOfferTypeChange}
          isChangeButtonVisible={isFormEditable}
        />

        {values.offerType && (
          <Stack direction='column' spacing={2}>
            <Box>
              <OfferNameAndDescriptionConfiguration>
                {currentFlowType === Flow_Types.Update && (
                  <OfferCampaignField canEdit={canEditCampaign} />
                )}
              </OfferNameAndDescriptionConfiguration>
            </Box>
            <OfferFulfillmentPriorityField />
            <Box>{renderAwardSection()}</Box>
            <Box>
              {values.offerType === OfferType.ProductPurchase && (
                <ProductPurchaseConfigure values={values.productPurchase} withCountBySelect />
              )}
              {values.offerType === OfferType.SpendBonus && <SpendBonusConfiguration />}
              {values.offerType === OfferType.BaseEarn && <BaseEarnConfiguration />}
              {values.offerType === OfferType.BaseEligibleEarn && <BaseEligibleEarnConfiguration />}
            </Box>
          </Stack>
        )}
      </OfferEarningMechanismConfigurationSection>

      {values.offerType && (
        <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
          <OfferConfigurationSection title='Limits'>
            {isVoucherRedemptionShown && (
              <VoucherExpirationDaysRedemption
                isVoucherRedemptionEnabled={isVoucherRedemptionEnabled}
                isVoucherRedemptionApplicable={isVoucherRedemptionApplicable}
                showAvatar
              />
            )}
            <AwardCount
              values={values}
              shouldShowTransactionLimit={getIsTransactionLimitSupported(values.offerType)}
              shouldShowTransactionFrequencyLimit={getIsTransactionFrequencyLimitSupported(
                values.offerType,
              )}
              defaultPeriod={getDefaultTransactionFrequencyPeriod(values.offerType)}
            />
          </OfferConfigurationSection>

          <OfferConfigurationSection title='Requirements'>
            <PaymentMethodConstraint values={values} isPaymentInstrumentConstraintEnabled={false} />

            {values.offerType === OfferType.ExternalFulfillment && <EmailRequiredConstraint />}

            {isOrderAheadEnabled && (
              <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
            )}

            <FulfillmentWindow values={values} />

            {shouldRenderMinimumPurchaseConstraint && (
              <MinimumPurchaseConstraints values={values} />
            )}

            {shouldRenderRequiresActivationConstraint && (
              <RequiresActivationConstraintWithLimits values={values.activation} />
            )}

            {values.offerType === OfferType.SpendBonus && (
              <StayVisibleWhileInProgress name='spendBonus.stayVisibleWhileInProgress.isEnabled' />
            )}

            {isAwardRestrictionsShown && <AwardRestrictions />}
          </OfferConfigurationSection>

          <AwardRestrictionsAwardTypeWatcher awardTypeFieldName='awardType' form={form} />

          <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
            <CustomData values={values.customData} subtitle={configureFormCustomDataSubtitle} />
            <ScanDataConfiguration values={values.scanDataConfiguration} submitting={submitting} />
          </OfferConfigurationSection>
        </OfferConfigurationWizardFormLayout>
      )}

      <OfferTypeWatcher form={form} />

      <EitherActivationOrAwardCount form={form} />
    </OfferForm>
  );
};
